import { format } from 'date-fns';
import { memo, useCallback } from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

export type WeekDayCellProps = {
  day: Date;
  isSelected: boolean;
  isToday: boolean;
  hasEvent: boolean;
  onSelectDate: (date: Date) => void;
};

function WeekDayCell({ day, isSelected, isToday, hasEvent, onSelectDate }: WeekDayCellProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const handlePress = useCallback(() => onSelectDate(day), [day, onSelectDate]);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      accessibilityLabel={STRINGS.guardian.kids.selectDate.replace(
        '{date}',
        format(day, 'MMMM d, yyyy'),
      )}
      onPress={handlePress}
      style={styles.root}>
      <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.weekdayLabel}>
        {format(day, 'EEE')}
      </Text>
      <Stack
        width={scale(36)}
        height={scale(36)}
        borderRadius={scale(10)}
        align="center"
        justify="center"
        marginTop={scale(2)}
        style={isSelected ? styles.daySelected : null}>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
          style={
            isSelected
              ? styles.dayNumberSelected
              : isToday
                ? styles.dayNumberToday
                : styles.dayNumber
          }>
          {format(day, 'd')}
        </Text>
      </Stack>
      <Stack style={[styles.dot, hasEvent ? null : styles.dotHidden]} />
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    flex: 1,
    alignItems: 'center',
  },
  weekdayLabel: {
    color: guardianColors['6'],
  },
  daySelected: {
    borderRadius: scale(10),
    backgroundColor: guardianColors['Main/Main'],
  },
  dayNumber: {
    color: guardianColors['10'],
  },
  dayNumberToday: {
    color: guardianColors['Main/Main'],
  },
  dayNumberSelected: {
    color: colors['0'],
  },
  dot: {
    width: scale(6),
    height: scale(6),
    flexShrink: 0,
    marginTop: scale(3),
    borderRadius: scale(3),
    backgroundColor: colors['Level 1'],
  },
  dotHidden: {
    opacity: 0,
  },
}));

export default memo(WeekDayCell);
